'use client';


import Header from '@/components/common/Header';
import HeroSection from './components/HeroSection';
import TimelineSection from './components/TimelineSection';
import FounderStory from './components/FounderStory';
import ValuesSection from './components/ValuesSection';
import CTASection from './components/CTASection';
import Footer from './components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';

export default function AboutPage() {
  const { t } = useLanguage();

  const heroData = {
    title: t('about.hero.title'),
    subtitle: t('about.hero.subtitle'),
    description: t('about.hero.description'),
    heroImage: "/assets/images/kru-1764348596582.jpg",
    heroImageAlt: "Professional bakery scene showing freshly baked golden-brown croissants with beautiful laminated layers on a granite counter, baker's hand in black clothing reaching toward the pastries, industrial bakery kitchen background with white walls and professional equipment"
  };

  const timelineData = {
    title: t('about.timeline.title'),
    subtitle: t('about.timeline.subtitle'),
    milestones: [
      {
        year: "2016",
        title: t('about.timeline.2016.title'),
        description: t('about.timeline.2016.description'),
        icon: "SparklesIcon"
      },
      {
        year: "2018",
        title: t('about.timeline.2018.title'),
        description: t('about.timeline.2018.description'),
        icon: "BuildingOfficeIcon"
      },
      {
        year: "2020",
        title: t('about.timeline.2020.title'),
        description: t('about.timeline.2020.description'),
        icon: "BuildingOfficeIcon"
      },
      {
        year: "2022",
        title: t('about.timeline.2022.title'),
        description: t('about.timeline.2022.description'),
        icon: "LightBulbIcon"
      },
      {
        year: "2024",
        title: t('about.timeline.2024.title'),
        description: t('about.timeline.2024.description'),
        icon: "TrophyIcon"
      }
    ]
  };

  const founderData = {
    founderName: t('about.founder.name'),
    founderTitle: t('about.founder.position'),
    founderImage: "/assets/images/ez222-1764350479867.jpg",
    founderImageAlt: "Professional business portrait of Ezgican Aydoğan in a navy blue suit with blue tie and decorative pocket square, seated confidently at a marble desk in an elegant office setting with warm lighting and modern professional background",
    story: [
      t('about.founder.story.p1'),
      t('about.founder.story.p2'),
      t('about.founder.story.p3'),
      t('about.founder.story.p4'),
      t('about.founder.story.p5')
    ],
    quote: t('about.founder.quote')
  };

  const valuesData = {
    title: t('about.values.title'),
    subtitle: t('about.values.subtitle'),
    values: [
      {
        icon: "HeartIcon",
        title: t('about.values.quality.title'),
        description: t('about.values.quality.description')
      },
      {
        icon: "UserGroupIcon",
        title: t('about.values.partnership.title'),
        description: t('about.values.partnership.description')
      },
      {
        icon: "ClockIcon",
        title: t('about.values.consistency.title'),
        description: t('about.values.consistency.description')
      },
      {
        icon: "SparklesIcon",
        title: t('about.values.innovation.title'),
        description: t('about.values.innovation.description')
      }
    ]
  };

  const ctaData = {
    title: t('about.cta.title'),
    description: t('about.cta.description'),
    primaryButtonText: t('about.cta.primaryButton'),
    primaryButtonLink: "/wholesale",
    secondaryButtonText: t('about.cta.secondaryButton'),
    secondaryButtonLink: "/contact"
  };

  const footerData = {
    sections: [
      {
        title: t('footer.section.company'),
        links: [
          { label: t('footer.link.aboutUs'), href: "/about" },
          { label: t('footer.link.products'), href: "/products" },
          { label: t('footer.link.production'), href: "/production" },
          { label: t('footer.link.contactUs'), href: "/contact" }
        ]
      },
      {
        title: t('footer.section.support'),
        links: [
          { label: t('footer.link.faq'), href: "/faq" },
          { label: t('footer.link.orderTracking'), href: "/order-tracking" }
        ]
      }
    ],
    contactInfo: {
      address: "Organize Sanayi Bölgesi, 3. Cadde No: 45, Pendik, İstanbul, Türkiye",
      phone: "+90 216 555 0123",
      email: "info@sidemgida.com"
    },
    socialLinks: {
      facebook: "https://facebook.com/sidemgida",
      instagram: "https://instagram.com/sidemgida",
      linkedin: "https://linkedin.com/company/sidemgida"
    }
  };

  return (
    <div data-component-id="src/app/about/page.tsx:144:4" data-component-path="src/app/about/page.tsx" data-component-line="144" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <Header data-component-id="src/app/about/page.tsx:145:6" data-component-path="src/app/about/page.tsx" data-component-line="145" data-component-file="page.tsx" data-component-name="Header" data-component-content="%7B%22elementName%22%3A%22Header%22%7D" />
      <main data-component-id="src/app/about/page.tsx:146:6" data-component-path="src/app/about/page.tsx" data-component-line="146" data-component-file="page.tsx" data-component-name="main" data-component-content="%7B%22elementName%22%3A%22main%22%2C%22className%22%3A%22pt-20%22%7D" className="pt-20">
        <HeroSection data-component-id="src/app/about/page.tsx:147:8" data-component-path="src/app/about/page.tsx" data-component-line="147" data-component-file="page.tsx" data-component-name="HeroSection" data-component-content="%7B%22elementName%22%3A%22HeroSection%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" {...heroData} />
        <TimelineSection data-component-id="src/app/about/page.tsx:148:8" data-component-path="src/app/about/page.tsx" data-component-line="148" data-component-file="page.tsx" data-component-name="TimelineSection" data-component-content="%7B%22elementName%22%3A%22TimelineSection%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" {...timelineData} />
        <FounderStory data-component-id="src/app/about/page.tsx:149:8" data-component-path="src/app/about/page.tsx" data-component-line="149" data-component-file="page.tsx" data-component-name="FounderStory" data-component-content="%7B%22elementName%22%3A%22FounderStory%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" {...founderData} />
        <ValuesSection data-component-id="src/app/about/page.tsx:150:8" data-component-path="src/app/about/page.tsx" data-component-line="150" data-component-file="page.tsx" data-component-name="ValuesSection" data-component-content="%7B%22elementName%22%3A%22ValuesSection%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" {...valuesData} />
        <CTASection data-component-id="src/app/about/page.tsx:151:8" data-component-path="src/app/about/page.tsx" data-component-line="151" data-component-file="page.tsx" data-component-name="CTASection" data-component-content="%7B%22elementName%22%3A%22CTASection%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" {...ctaData} />
      </main>
      <Footer data-component-id="src/app/about/page.tsx:153:6" data-component-path="src/app/about/page.tsx" data-component-line="153" data-component-file="page.tsx" data-component-name="Footer" data-component-content="%7B%22elementName%22%3A%22Footer%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" {...footerData} />
    </div>
  );
}